import { Store } from '../store'; // eslint-disable-line no-unused-vars
import { makeAutoObservable, runInAction } from 'mobx';
import { API_ENDPOINTS } from '../../api/endpoints';
import { UI_OPTIONS_UTILS } from '../../utils/constants/uiOptions';
import { mapOptions } from '../../api/dataMappers';
import { HEADER_MESSAGE_TYPES } from '../../utils/constants/header';
import { OPTION_KEYS } from '../../utils/constants/optionKeys';
import { formatNumber } from '../../utils/utils';
import { PUNCTUATION } from '../../utils/constants/notation';
import { paths } from '../../utils/constants/routes';
import { ACCOUNT_TYPES } from '../../utils/constants/auth';
import { PROFILER_SEARCH_FIELDS } from '../../utils/constants/fields';
import { isRevenueMetric } from '../../utils/constants/profilerFinancials';
import { isInDevelopment } from '../../ga4/constants';

class UtilsStore {
  /**
   * @param {Store} root
   */
  constructor(root) {
    makeAutoObservable(this);
    this.root = root;

    window.addEventListener('resize', () => {
      this.setWindowWidth(window.innerWidth);
      this.setWindowHeight(window.innerHeight);
    });

    this.loadOptions();
  }

  profilerFinancialsFormatUnit =
    localStorage.getItem('profilerFinancialsFormatUnit') || 'euroMillions';

  setProfilerFinancialsFormatUnit = (value = 'euroMillions') => {
    runInAction(() => {
      this.profilerFinancialsFormatUnit = value;
      localStorage.setItem('profilerFinancialsFormatUnit', value);
    });
  };

  // TD-2352: whether per-field data-source indicators are shown across company-data pages.
  // Default OFF; persisted per device in localStorage (v2/TD-2364 moves this to user config).
  showDataSources = localStorage.getItem('td_showDataSources') === '1';

  setShowDataSources = (value = false) => {
    runInAction(() => {
      this.showDataSources = value;
      localStorage.setItem('td_showDataSources', value ? '1' : '0');
    });
  };

  formatProfilerCurrency = (value, emptyValue = PUNCTUATION.EMPTY_VALUE) => {
    if (value && !isNaN(value)) {
      if (value === 0 || value === '0') {
        return '0';
      }

      const formattedValue = formatNumber(
        (
          Number(value) / (this.profilerFinancialsFormatUnit === 'euroMillions' ? 1000000 : 1000)
        ).toFixed(1),
        PUNCTUATION.COMMA,
        true
      );

      if (formattedValue === PUNCTUATION.EMPTY_VALUE) {
        return emptyValue;
      }

      return formattedValue;
    }

    return value || emptyValue;
  };

  isLoadingOptions = false;
  options = null;

  loadOptions = () => {
    if (this.isLoadingOptions) {
      return;
    }

    this.isLoadingOptions = true;
    const res = { success: false };
    this.root.makeRequest({
      endpoint: API_ENDPOINTS.GET_PUBLIC_OPTIONS,
      onSuccess: (response) => {
        this.options = { ...UI_OPTIONS_UTILS, ...mapOptions(response) };
        res.success = true;
      },
      onError: () => {
        this.isLoadingOptions = false;
        setTimeout(() => {
          this.loadOptions(); // requests with 1 second interval untill we get the options
        }, 1000);
      },
      onFinally: () => {
        if (res.success) {
          this.isLoadingOptions = false;
        }
      }
    });
  };

  getOptionName = (data = {}, fieldConfig = {}, optionKey = '') => {
    const options = this.options?.[optionKey || fieldConfig.VALUES_KEY];
    const fieldData = data?.[fieldConfig.NAME] || data;
    if (Array.isArray(fieldData)) {
      return fieldData?.map?.((v) => options?.find?.((o) => o.value === v)?.name || v) || [];
    } else {
      return options?.find?.((o) => o.value === fieldData)?.name || fieldData;
    }
  };

  getIndustryCodesDisplayNames = (listIndustryCodesValues = []) => {
    const rawTree = this.options?.[OPTION_KEYS.PROFILER_INDUSTRY_CODES]?.rawList;
    if (!rawTree) return listIndustryCodesValues;

    const selected = new Set(listIndustryCodesValues);
    const compressed = new Set();

    const compressTree = (node) => {
      if (!node.children || node.children.length === 0) {
        if (selected.has(node.value)) {
          compressed.add(node.value);
          return true;
        }
        return false;
      }

      let allChildrenSelected = true;

      for (const child of node.children) {
        const childSelected = compressTree(child);
        allChildrenSelected = allChildrenSelected && childSelected;
      }

      if (allChildrenSelected) {
        for (const child of node.children) {
          compressed.delete(child.value);
        }
        compressed.add(node.value);
        return true;
      }

      if (selected.has(node.value)) {
        compressed.add(node.value);
        return true;
      }

      return false;
    };

    for (const topNode of rawTree) {
      compressTree(topNode);
    }

    const cleanedUpCodes = Array.from(compressed);

    const codeToNameMap = Object.fromEntries(
      this.options[OPTION_KEYS.PROFILER_INDUSTRY_CODES]?.flatList?.map(({ value, name }) => [
        value,
        name
      ]) ?? []
    );

    return this.options[OPTION_KEYS.PROFILER_INDUSTRY_CODES]?.flatList
      ? cleanedUpCodes.map((code) => codeToNameMap[code])
      : listIndustryCodesValues;
  };

  shortenList = (list = [], maxLength = 3) => {
    if (list.length <= maxLength) {
      return list;
    }

    return list.slice(0, maxLength).concat(['...']);
  };

  windowWidth = window.innerWidth;
  setWindowWidth = (width = 0) => (this.windowWidth = width);
  windowHeight = window.innerHeight;
  setWindowHeight = (height = 0) => (this.windowHeight = height);

  isSessionExpired = false;
  setIsSessionExpired = (value = false) => {
    if (value === this.isSessionExpired) {
      return;
    }

    runInAction(() => {
      this.isSessionExpired = value;
    });
  };

  isMobileHamburgerOpen = false;
  setIsMobileHamburgerOpen = (value = false, forceSkipRestoreLock = false) => {
    if (value !== this.isMobileHamburgerOpen) {
      runInAction(() => {
        this.isMobileHamburgerOpen = value;
      });

      if (this.isMobileHamburgerOpen) {
        this.lockScroll(true, true);
        this.isLockScroll = null;
        window.scrollTo({ top: 0 });
      } else {
        if (forceSkipRestoreLock) {
          this.returnToPosition = null;
          this.isLockScroll = false;
          this.lockScrollY = null;
          this.saveScrollPosition = false;
        } else {
          this.lockScroll(false);
        }
      }
    }
  };

  isDesktopMenuOpen = false;
  setIsDesktopMenuOpen = (value = false) => {
    if (value !== this.isDesktopMenuOpen) {
      runInAction(() => {
        this.isDesktopMenuOpen = value;
      });
    }
  };

  headerPinnedMessage = { message: '', type: HEADER_MESSAGE_TYPES.SUCCESS, id: null };
  setHeaderPinnedMessage = (message = '', type = HEADER_MESSAGE_TYPES.WARNING, id = null) => {
    runInAction(() => {
      this.headerPinnedMessage = { message, type, id };
    });
  };

  headerMessageTimestamp = 0;
  headerMessage = { message: '', type: HEADER_MESSAGE_TYPES.SUCCESS, isFading: false, persistent: false };
  setHeaderMessage = (message = '', type = HEADER_MESSAGE_TYPES.SUCCESS, timeout = 3_000) => {
    runInAction(() => {
      this.headerMessage = { message, type, isFading: false, persistent: !timeout };
      this.headerMessageTimestamp = new Date().getTime();
    });

    // timeout:0 (falsy) → persistent message that stays until the user dismisses
    // it via the header ✕ (clearHeaderMessage). Used for the colleague-took-over
    // logout notice so a user who stepped away still sees why they were signed
    // out when they return (TD-2239).
    if (!timeout) {
      return;
    }

    setTimeout(() => {
      if (this.headerMessageTimestamp + timeout <= new Date().getTime()) {
        runInAction(() => {
          this.headerMessage = { ...this.headerMessage, isFading: true };
        });
      }
    }, timeout + 1);

    setTimeout(
      () => {
        if (this.headerMessageTimestamp + timeout + 1_000 <= new Date().getTime()) {
          runInAction(() => {
            this.headerMessage = { message: '', type };
          });
        }
      },
      timeout + 1 + 1_000
    );
  };

  clearHeaderMessage = () => {
    runInAction(() => {
      this.headerMessage = { message: '', type: this.headerMessage.type, isFading: false, persistent: false };
      this.headerMessageTimestamp = 0;
    });
  };

  isMobileFooterHidden = false;
  setIsMobileFooterHidden = (value = false) => {
    if (value !== this.isMobileFooterHidden) {
      runInAction(() => {
        this.isMobileFooterHidden = value;
      });
    }
  };

  isLockScroll = false;
  lockScrollY = null;
  saveScrollPosition = false;
  returnToPosition = false;
  lockScroll = (lock = true, savePosition = false) => {
    if (lock) {
      this.lockScrollY = window.scrollY;
      this.saveScrollPosition = savePosition;
      this.isLockScroll = true;
    } else {
      this.returnToPosition = this.saveScrollPosition ? this.lockScrollY : null;
      this.isLockScroll = false;
      this.lockScrollY = null;
      this.saveScrollPosition = false;
    }
  };

  isRefreshingApp = false;
  setIsRefreshingApp = (value = true) => {
    runInAction(() => {
      this.isRefreshingApp = value;
    });
  };

  defaultRefresh = async () => {
    setTimeout(() => window.location.reload(), 1000);
    return new Promise((res) => setTimeout(() => res(true), 2000));
  };

  onPageRefresh = null; // async promise if refreshing data from page

  setOnPageRefresh = (onRefresh = null) => {
    runInAction(() => {
      this.onPageRefresh = onRefresh;
    });
  };

  refreshPage = async () => {
    this.setIsRefreshingApp(true);
    if (typeof this.onPageRefresh === 'function') {
      return this.onPageRefresh();
    } else {
      return this.defaultRefresh();
    }
  };

  openProfilerSearchCriteriaAndFillFromCompanyDetails = (company, consolidated, individual) => {
    const fields = {};

    const consolidatedProfitAndLoss = consolidated?.groups?.find((g) => g.id === 'profitAndLoss');
    const individualProfitAndLoss = individual?.groups?.find((g) => g.id === 'profitAndLoss');

    // Revenue is identified by the BE metric_group (TD-2401) — unifies TC/COGS
    // accounting variants and self-disclosed revenue under one dimension.
    const getLatestEntry = (items) => {
      const item = items?.find(isRevenueMetric);
      if (!item?.entries?.length) return null;
      return item.entries.reduce((latest, entry) => (entry._year > latest._year ? entry : latest));
    };

    const consolidatedLatestRevenueEntry = getLatestEntry(consolidatedProfitAndLoss?.items);
    const individualLatestRevenueEntry = getLatestEntry(individualProfitAndLoss?.items);

    let originalRevenue;

    if (consolidatedLatestRevenueEntry && individualLatestRevenueEntry) {
      originalRevenue =
        individualLatestRevenueEntry._year + 1 > consolidatedLatestRevenueEntry._year
          ? individualLatestRevenueEntry.value
          : consolidatedLatestRevenueEntry.value;
    } else if (consolidatedLatestRevenueEntry) {
      originalRevenue = consolidatedLatestRevenueEntry.value;
    } else if (individualLatestRevenueEntry) {
      originalRevenue = individualLatestRevenueEntry.value;
    } else {
      originalRevenue = company._revenue;
    }

    // TD-1809: Handle null/undefined revenue - leave fields empty instead of NaN
    const hasValidRevenue = originalRevenue != null && !Number.isNaN(originalRevenue);
    const minRevenue = hasValidRevenue ? ((originalRevenue * 0.25) / 1_000_000).toFixed(2) : '';
    const maxRevenue = hasValidRevenue ? ((originalRevenue * 2) / 1_000_000).toFixed(2) : '';

    fields[PROFILER_SEARCH_FIELDS.REVENUE_MIN.NAME] = minRevenue;
    fields[PROFILER_SEARCH_FIELDS.REVENUE_MAX.NAME] = maxRevenue;
    fields[PROFILER_SEARCH_FIELDS.INDUSTRY.NAME] = company.industries.join(', ');
    fields[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.NAME] = company.valueChain.join(', ');
    fields[PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.NAME] = company.products.join(', ');

    if (isInDevelopment) {
      const styles = {
        header: 'color: #60a5fa; font-weight: bold; font-size: 14px;',
        section: 'color: #d63384; font-weight: bold;',
        label: 'color: #0d6efd; font-weight: bold;',
        info: 'color: #a78bfa;',
        muted: 'color: #9ca3af;'
      };

      console.log(
        '%c🚀 DEV/STG Logs - openProfilerSearchCriteriaAndFillFromCompanyDetails [ START ]',
        styles.header
      );

      console.log('%c📊 Data Objects:', styles.section);
      console.log('%ccompany:', styles.label, company);
      console.log('%cconsolidated:', styles.label, consolidated);
      console.log('%cindividual:', styles.label, individual);

      console.log('%c📋 Business Logic:', styles.section);
      console.log(
        '%cOriginal data source:',
        styles.info,
        'company (industryKeywords, valueChainKeywords, productPortfolioKeywords)'
      );
      console.log('%cRevenue calculation logic:', styles.info);
      console.log('%c  • Primary: consolidated or individual financials', styles.muted);
      console.log(
        '%c  • Exception: if consolidated revenue > 1 year older than individual',
        styles.muted
      );
      console.log(
        '%c  • Fallback from company: revenue → revenueBasic → grossProfit',
        styles.muted
      );

      console.log('%c🔧 Fields Configuration:', styles.section);
      console.log('%cfields:', styles.label, fields);

      console.log(
        '%c✅ DEV/STG Logs - openProfilerSearchCriteriaAndFillFromCompanyDetails [  END  ]',
        styles.header
      );
      console.log('');
    }

    localStorage.setItem('profiler-search-criteria-pre-fill-fields', JSON.stringify(fields));
    window.open(
      window.location.origin +
        (this.root.authStore.userAccountType === ACCOUNT_TYPES.INVESTOR ? paths.PROFILER : ''),
      '_blank'
    );
  };

  getProfilerSearchCriteriaPreFillFields = () => {
    const foundInStorage = localStorage.getItem('profiler-search-criteria-pre-fill-fields');
    if (foundInStorage) {
      const fields = JSON.parse(foundInStorage);
      setTimeout(() => {
        localStorage.removeItem('profiler-search-criteria-pre-fill-fields');
      }, 0);
      return fields;
    }

    return null;
  };

  /**
   * Build company details URL with query parameters
   * @param {string} companyId - Company ID for the URL path
   * @param {Object} options - Optional query parameters
   * @param {string} options.tab - Initial tab to display (e.g., 'shareholders', 'contacts')
   * @param {string} options.ref - Back button label (e.g., entity name or 'Search Results')
   * @param {string} options.searchKey - Search query for name search (uses GET_PROFILER_COMPANY_FROM_SEARCH)
   * @param {string} options.searchId - Search ID for characteristics/industry search (uses GET_PROFILER_COMPANY_SUPPLEMENTARY_DATA)
   * @param {string} options.comparablesId - Comparables ID for caching comparable companies results (TD-1816)
   * @returns {string} URL path with query string
   */
  buildCompanyDetailsUrl = (companyId, options = {}) => {
    const params = new URLSearchParams();
    if (options.tab) params.set('tab', options.tab);
    if (options.ref) params.set('ref', options.ref);
    if (options.searchKey) params.set('searchKey', options.searchKey);
    if (options.searchId) params.set('searchId', options.searchId);
    if (options.comparablesId) params.set('comparablesId', options.comparablesId);
    const queryString = params.toString();
    return `${paths.PROFILER_DETAILS}/${companyId}${queryString ? '?' + queryString : ''}`;
  };

  /**
   * Get company details path for same-tab navigation
   * @param {string} companyId - Company ID
   * @param {Object} options - Query parameters (tab, ref)
   * @returns {string} URL path for navigate()
   */
  getCompanyDetailsPath = (companyId, options = {}) => {
    return this.buildCompanyDetailsUrl(companyId, options);
  };

  /**
   * Open company details in new tab
   * @param {string} companyId - Company ID
   * @param {Object} options - Query parameters (tab, ref)
   */
  openCompanyDetails = (companyId, options = {}) => {
    const url = this.buildCompanyDetailsUrl(companyId, options);
    window.open(window.location.origin + url, '_blank');
  };

  /**
   * Build individual details URL with query parameters
   * @param {string} individualId - Individual ID for the URL path
   * @param {Object} options - Optional query parameters
   * @param {string} options.ref - Back button label (e.g., company name)
   * @returns {string} URL path with query string
   */
  buildIndividualDetailsUrl = (individualId, options = {}) => {
    const params = new URLSearchParams();
    if (options.ref) params.set('ref', options.ref);
    const queryString = params.toString();
    return `${paths.INDIVIDUAL_DETAILS}/${individualId}${queryString ? '?' + queryString : ''}`;
  };

  /**
   * Get individual details path for same-tab navigation
   * @param {string} individualId - Individual ID
   * @param {Object} options - Query parameters (ref)
   * @returns {string} URL path for navigate()
   */
  getIndividualDetailsPath = (individualId, options = {}) => {
    return this.buildIndividualDetailsUrl(individualId, options);
  };

  /**
   * Open individual details in new tab
   * @param {string} individualId - Individual ID
   * @param {Object} options - Query parameters (ref)
   */
  openIndividualDetails = (individualId, options = {}) => {
    const url = this.buildIndividualDetailsUrl(individualId, options);
    window.open(window.location.origin + url, '_blank');
  };

  /**
   * Open an in-app path in a new browser tab.
   *
   * Companion to openCompanyDetails/openIndividualDetails for destinations that
   * are already a built app path (e.g. a project or shared-project deep link)
   * rather than an entity id. `path` must be app-absolute (begin with '/').
   * @param {string} path
   */
  openInNewTab = (path) => {
    if (!path) {
      return;
    }
    window.open(window.location.origin + path, '_blank');
  };
}

export default UtilsStore;
